import { isEmpty } from 'lodash-es';
import React, { ReactNode } from 'react';
import { JSX } from 'react';

import { generateCountryGroupOptionsList } from '~/src/dash/api/country-group';
import { generateAudienceSegmentOptionsList } from '~/src/dash/apiV2/audienceSegments';
import { generateContextualSegmentOptionsList } from '~/src/dash/apiV2/contextualSegments';
import {
  ITargetingPreset,
  TargetingPresetKey,
  TargetingPresetKeys,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import {
  generateOptionsListAsPromise,
  ISelectOption,
} from '~/src/dash/components/dropdowns/common';
import {
  convertDaypartingUI,
  convertDevicesResponseValue,
  convertDevicesSelectedValue,
  convertDeviceSubTypesResponseValue,
  convertDeviceSubTypesSelectedValue,
  convertDeviceTypesResponseValue,
  convertDeviceTypesSelectedValue,
  convertLivestreamResponseValue,
  convertLivestreamSelectedValue,
  convertLocalTargetingUI,
  generateContentPlaceholder,
  generateCustomPlaceholder,
  generateGenericPlaceholder,
  IParameterOption,
  parseDaypartingForUI,
  parseLocalTargetingForUI,
  validateTextInput,
} from '~/src/dash/components/forms/formBuilder/common';
import {
  AppBundleIDsSitePagesEditor,
  IAppBundleIDsSitePagesEditorProps,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/AppBundleIDsSitePagesEditor';
import {
  convertSetIntoSingleNumber,
  convertSetIntoString,
  convertSingleNumberIntoArray,
  convertStringIntoArray,
  removeEmojiPrefixFromLabel,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import { DaypartingBuilder } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder';
import { GDPRSelect } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/GDPRSelect';
import {
  ILocalTargetingBuilderProps,
  LocalTargetingBuilder,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder';
import { RegionsEditor } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/RegionsEditor';
import {
  ISelectWithIncludeAndLogicProps,
  SelectWithIncludeAndLogic,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/SelectWithIncludeAndLogic';
import {
  ITextInputWithIncludeProps,
  TextInputWithInclude,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/TextInputWithInclude';
import { IItemIDNameRoute } from '~/src/dash/components/tables/IDNameTable/common';
import { Span, TKey, Text } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { ItemRouteType } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { generateDMAOptions } from '~/src/dash/utils/countries';
import {
  DeviceOptions,
  DeviceORTBTypeOptions,
  ExtendedIDTargetingTypeOptions,
  generateCountryOptions,
  generateDeviceSubTypeOptionsList,
  TargetBoolOptions,
  TargetingCCPAOptions,
  TargetingCOPPAOptions,
  TargetingDeviceIDTypeOptions,
  TargetingGDPRConsentOptions,
  TargetingGDPROptions,
  TargetingLiveStreamOptions,
} from '~/src/dash/utils/options';
import { isPublisherSamsung } from '~/src/dash/utils/publishers';
import {
  AppBundleSitePagesSummary,
  IAppBundleSitePagesSummaryProps,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/AppBundleSitePagesSummary';
import { DaypartingSummary } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/DaypartingSummary';
import { GDPRSummary } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/GDPRSummary';
import {
  IIncludeExcludeStringProps,
  IncludeExcludeString,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/IncludeExcludeString';
import { LocalTargetingSummary } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/LocalTargetingSummary';
import { SelectedOptionsWithLogic } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/SelectedOptionsWithLogic';

export type TargetingPresetSelectCategory =
  | 'Content'
  | 'Geo'
  | 'Time'
  | 'Privacy'
  | 'Custom'
  | 'Devices'
  | 'App'
  | 'Deals'
  | 'Streams'
  | 'Segments'
  | 'App Bundle IDs & Site Pages'
  | 'Site Pages'
  | 'Site Domains'
  | 'Extended IDs';

export type TargetingPresetCategory = 'Supply' | 'Users' | 'Customs';

export const TargetingPresetCategoryKeys: Record<string, TargetingPresetCategory> = {
  SUPPLY: 'Supply',
  USERS: 'Users',
  CUSTOMS: 'Customs',
};

export type TargetingPresetParamKey =
  // CONTENT
  | 'ContentChannel'
  | 'ContentDistributor'
  | 'ContentGenre'
  | 'ContentID'
  | 'ContentNetwork'
  | 'ContentProducer'
  | 'ContentRating'
  | 'ContentSeries'
  | 'ContentTitle'
  | 'ContentCategory'
  | 'ContentLanguage'
  // GEO
  | 'TargetedRegions' // COUNTRY GROUPS
  | 'TargetedCountries'
  | 'LocalTargetingAllowed'
  | 'LocalTargetingDisallowed'
  // DAYPARTING
  | 'Dayparting'
  // PRIVACY
  | 'COPPA'
  | 'LimitedAdTracking'
  | 'CCPA'
  | 'GDPR'
  | 'TargetingDeviceIDType'
  // CUSTOM
  | 'Custom4'
  | 'Custom5'
  | 'Custom6'
  | 'Custom7'
  | 'Custom8'
  | 'Custom9'
  | 'Custom10'
  | 'Custom11'
  | 'Custom12'
  | 'Custom13'
  | 'Custom14'
  | 'Custom15'
  | 'Custom16'
  | 'Custom17'
  | 'Custom18'
  | 'CustomTargeting'
  // DEVICE
  | 'TargetedDevices'
  | 'TargetedDeviceTypes'
  | 'TargetedDeviceSubtypes'
  | 'IPFilter'
  // APP and SITE PAGES
  | 'AppBundleIDsSitePages'
  // STREAMS
  | 'TargetedLivestreams'
  // SEGMENTS
  | 'AudienceSegments'
  | 'ContextualSegments'
  // SITE DOMAINS
  | 'SiteDomains';
// EXTENDED IDS
// | 'TargetingUID20TargetingType'
// | 'TargetingGooglePairIDTargetingType'
// | 'TargetingLiveRampATSTargetingType'
// | 'TargetingIrisSignalTargetingType';
// | 'TargetingSamsungIdentityTargetingType'
// | 'TargetingSamsungEPGMetadataTargetingType';

export const TargetingPresetParamKeys: Record<string, TargetingPresetParamKey> = {
  // CONTENT
  CONTENT_CHANNEL: 'ContentChannel',
  CONTENT_DISTRIBUTOR: 'ContentDistributor',
  CONTENT_GENRE: 'ContentGenre',
  CONTENT_ID: 'ContentID',
  CONTENT_NETWORK: 'ContentNetwork',
  CONTENT_PRODUCER: 'ContentProducer',
  CONTENT_RATING: 'ContentRating',
  CONTENT_SERIES: 'ContentSeries',
  CONTENT_TITLE: 'ContentTitle',
  CONTENT_CATEGORY: 'ContentCategory',
  CONTENT_LANGUAGE: 'ContentLanguage',
  // GEO
  TARGETED_REGIONS: 'TargetedRegions', // COUNTRY GROUPS
  TARGETED_COUNTRIES: 'TargetedCountries',
  LOCAL_TARGETING_ALLOWED: 'LocalTargetingAllowed',
  LOCAL_TARGETING_DISALLOWED: 'LocalTargetingDisallowed',
  // DAYPARTING
  DAYPARTING: 'Dayparting',
  // PRIVACY
  COPPA: 'COPPA',
  LIMITED_AD_TRACKING: 'LimitedAdTracking',
  CCPA: 'CCPA',
  GDPR: 'GDPR',
  TARGETING_DEVICE_ID_TYPE: 'TargetingDeviceIDType',
  // CUSTOM
  CUSTOM_4: 'Custom4',
  CUSTOM_5: 'Custom5',
  CUSTOM_6: 'Custom6',
  CUSTOM_7: 'Custom7',
  CUSTOM_8: 'Custom8',
  CUSTOM_9: 'Custom9',
  CUSTOM_10: 'Custom10',
  CUSTOM_11: 'Custom11',
  CUSTOM_12: 'Custom12',
  CUSTOM_13: 'Custom13',
  CUSTOM_14: 'Custom14',
  CUSTOM_15: 'Custom15',
  CUSTOM_16: 'Custom16',
  CUSTOM_17: 'Custom17',
  CUSTOM_18: 'Custom18',
  CUSTOM_TARGETING: 'CustomTargeting',
  // DEVICE
  TARGETED_DEVICES: 'TargetedDevices',
  TARGETED_DEVICE_TYPES: 'TargetedDeviceTypes',
  TARGETED_DEVICE_SUBTYPES: 'TargetedDeviceSubtypes',
  IP_FILTER: 'IPFilter',
  // APP and SITE PAGES
  APP_BUNDLE_IDS_SITE_PAGES: 'AppBundleIDsSitePages',
  // STREAMS
  TARGETED_LIVESTREAMS: 'TargetedLivestreams',
  // SEGMENTS
  AUDIENCE_SEGMENTS: 'AudienceSegments',
  CONTEXTUAL_SEGMENTS: 'ContextualSegments',
  // SITE DOMAINS
  SITE_DOMAINS: 'SiteDomains',
  // EXTENDED IDS
  // TARGETING_UID20_TARGETING_TYPE: 'TargetingUID20TargetingType',
  // TARGETING_GOOGLE_PAIR_ID_TARGETING_TYPE: 'TargetingGooglePairIDTargetingType',
  // TARGETING_LIVE_RAMP_ATS_TARGETING_TYPE: 'TargetingLiveRampATSTargetingType',
  // TARGETING_IRIS_SIGNAL_TARGETING_TYPE: 'TargetingIrisSignalTargetingType',
  // TARGETING_SAMSUNG_IDENTITY_TARGETING_TYPE: 'TargetingSamsungIdentityTargetingType',
  // TARGETING_SAMSUNG_EPG_METADATA_TARGETING_TYPE: 'TargetingSamsungEPGMetadataTargetingType',
};

const customTooltipMessage = `Checks for custom parameters set in your request to Publica's endpoint and includes or excludes the bidder. Can be comma separated.`;

export const convertSingleNumericIDForSummaryZeroDefault = (
  value: number,
  options: ISelectOption[],
) => {
  if (!value) return '';
  const selectedOption = options.find((option) => option.id === value);
  if (selectedOption && !isEmpty(selectedOption)) {
    return selectedOption.label;
  }
  return '';
};

export const convertSingleNumericIDForSummaryOneDefault = (
  value: number,
  options: ISelectOption[],
): string => {
  if (!value || value === 1) return '';
  const selectedOption = options.find((option) => option.id === value);
  if (selectedOption && !isEmpty(selectedOption)) {
    return selectedOption.label;
  }
  return '';
};

const convertNumericIDsForSummary = (value: string, options: ISelectOption[]): string => {
  if (!value) return '';
  const selectedIDs = new Set(value.split(',').map((v) => Number(v)));
  if (selectedIDs.size === options.length) return '';
  const selectedLabels: string[] = [];
  options.forEach(({ id, label }) => {
    if (selectedIDs.has(id as number)) {
      selectedLabels.push(label);
    }
  });
  return selectedLabels.join(', ');
};

const convertNumericIDsForSummaryZeroAll = (value: string, options: ISelectOption[]) => {
  if (!value || value === '0') return '';
  const selectedIDs = new Set(value.split(',').map((v) => Number(v)));
  if (selectedIDs.size === options.length) return '';
  const selectedLabels: string[] = [];
  options.forEach(({ id, label }) => {
    if (selectedIDs.has(id as number)) {
      selectedLabels.push(label);
    }
  });
  return selectedLabels.join(', ');
};

const convertCountryIDsForSummary = (value: string, options: ISelectOption[]) => {
  if (!value) return '';
  const selectedIDs = new Set(value.split(',').map((v) => v.toLowerCase()));
  const selectedLabels: string[] = [];
  options.forEach((option) => {
    if (selectedIDs.has(option.id as string)) {
      selectedLabels.push(removeEmojiPrefixFromLabel(option));
    }
  });
  return selectedLabels.join(', ');
};

const convertIDNameForSummary = (value: IItemIDNameRoute[]) => {
  if (!value || isEmpty(value)) return '';
  const selectedLabels: string[] = [];
  value.forEach((v) => {
    selectedLabels.push(v.Name);
  });
  return selectedLabels.join(', ');
};

const CustomAdvancedTargetingTooltip = (): NonNullable<ReactNode> | undefined => (
  <>
    <Text small color={publicaColors.white}>
      You can enter an exact value or use advanced patterns for more flexible targeting, can be
      comma separated.
    </Text>
    <ul>
      <li>
        <Text small color={publicaColors.white}>
          <Span bold>Exact Match:</Span> Enter a specific value to bid only on inventory that
          matches it exactly.
        </Text>
      </li>
      <li>
        <Text small color={publicaColors.white}>
          <Span bold>Advanced Targeting Patterns:</Span>
        </Text>
      </li>
      <li>
        <Text small color={publicaColors.white}>
          <Span bold>{'{{contain:value}}'}</Span> : Target inventory where the field's text contains
          the value you enter. Example: To target inventory about cars, enter{' '}
          <Span bold>{'{{contain:car}}'}</Span>
        </Text>
      </li>
      <li>
        <Text small color={publicaColors.white}>
          <Span bold>{'{{min:max}}'}</Span> : Target a numeric range. Bids will only be placed on
          inventory where the field's value falls between the minimum and maximum values
          (inclusive). Example: To target ages 25-35, enter <Span bold>{'{{25:35}}'}</Span>
        </Text>
      </li>
      <li>
        <Text small color={publicaColors.white}>
          <Span bold>{'{{null}}'}</Span> : Target inventory where the field is empty or missing.
          This pattern cannot be combined with any other logic. Example: To bid on inventory that's
          missing a user ID, enter <Span bold>{'{{null}}'}</Span>
        </Text>
      </li>
    </ul>
  </>
);

interface IConvertedPayload {
  value: any;
  errors: string[];
}

interface ICustomPresetConversion {
  convertResponse: (value: any, session: ISessionState) => any;
  convertPayload: (value: any) => IConvertedPayload;
  uiKey: TargetingPresetKey;
  apiKey: TargetingPresetKey;
}

interface ICustomValidation {
  validateKey: TargetingPresetKey;
  validateFn: (value: any, parameterLabel: string) => string[];
}

export interface ITargetingPresetParam {
  key: TargetingPresetParamKey;
  label: string; // displayed title
  categoryKey: TargetingPresetCategory; // section category
  selectCategoryKey: TargetingPresetSelectCategory; // dropdown category
  isSuperUserOnly?: boolean; // make sure to also populate checkIfHidden
  checkIfHidden?: (publisherID: string, isSuperUser: boolean) => boolean; // check if publisher specific
  Component: (props: any) => JSX.Element;
  props?: Record<string, any>;
  apiKeys: TargetingPresetKey[]; // DO NOT INCLUDE KEYS THAT ARE USED IN CUSTOMUICONVERSIONS! IMPORTANT!!!
  customUIConversions?: ICustomPresetConversion;
  customValidation?: ICustomValidation;
  SummaryComponent: (props: any) => JSX.Element;
  summaryProps?: Record<string, any>;
  tKey: TKey;
  selectCategoryTKey: TKey;
}

export const TargetingPresetParameters: Record<TargetingPresetParamKey, ITargetingPresetParam> = {
  // SUPPLY CATEGORY
  // DEVICE
  TargetedDevices: {
    key: TargetingPresetParamKeys.TARGETED_DEVICES,
    label: 'Targeted Devices',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    selectCategoryTKey: 'Devices',
    tKey: 'TargetedDevices',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICES,
      TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Devices Selected',
      singularLabel: 'Device',
      listLabel: 'Devices',
      listKey: 'Devices',
      valueKey: TargetingPresetKeys.TARGETING_DEVICES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
      convertResponseValue: convertDevicesResponseValue,
      convertSelectedValue: convertDevicesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICES,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'Devices',
      valueKey: TargetingPresetKeys.TARGETING_DEVICES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
      convertValue: convertNumericIDsForSummaryZeroAll,
    },
  },
  TargetedDeviceTypes: {
    key: TargetingPresetParamKeys.TARGETED_DEVICE_TYPES,
    label: 'Targeted Device Types',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    selectCategoryTKey: 'Devices',
    tKey: 'TargetedDeviceTypes',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device Types Selected',
      singularLabel: 'Device Type',
      listLabel: 'Device Types',
      listKey: 'DeviceTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceTypesResponseValue,
      convertSelectedValue: convertDeviceTypesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
      isSelectionRequired: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'DeviceTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
      convertValue: convertNumericIDsForSummary,
    },
  },
  TargetedDeviceSubtypes: {
    key: TargetingPresetParamKeys.TARGETED_DEVICE_SUBTYPES,
    label: 'Targeted Device Subtypes',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    selectCategoryTKey: 'Devices',
    tKey: 'TargetedDeviceSubtypes',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device SubTypes Selected',
      singularLabel: 'Device SubType',
      listLabel: 'Device SubTypes',
      listKey: 'DeviceSubTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceSubTypesResponseValue,
      convertSelectedValue: convertDeviceSubTypesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    checkIfHidden: (publisherID) => !isPublisherSamsung(publisherID),
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'DeviceSubTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
      convertValue: convertNumericIDsForSummaryZeroAll,
    },
  },
  IPFilter: {
    key: TargetingPresetParamKeys.IP_FILTER,
    label: 'IP Filters *',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    selectCategoryTKey: 'Devices',
    tKey: 'IPFiltersWithAsterisk',
    apiKeys: [TargetingPresetKeys.IP_FILTER],
    isSuperUserOnly: true,
    checkIfHidden: (_, isSuperUser) => !isSuperUser,
    customValidation: {
      validateKey: TargetingPresetKeys.IP_FILTER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.IP_FILTER,
      placeholder: generateGenericPlaceholder('IP Filters'),
      valueKey: TargetingPresetKeys.IP_FILTER,
      tooltip: `Match this value to the IP set in your request to Publica's endpoint. Can be comma separated.`,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.IP_FILTER,
    } as IIncludeExcludeStringProps,
  },
  // CONTENT
  ContentChannel: {
    key: TargetingPresetParamKeys.CONTENT_CHANNEL,
    label: 'Content Channel',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentChannel',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_CHANNEL,
      placeholder: generateContentPlaceholder('Content Channel'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentDistributor: {
    key: TargetingPresetParamKeys.CONTENT_DISTRIBUTOR,
    label: 'Content Distributor',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentDistributor',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_DISTRIBUTOR,
      placeholder: generateContentPlaceholder('Content Distributor'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentGenre: {
    key: TargetingPresetParamKeys.CONTENT_GENRE,
    label: 'Content Genre',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentGenre',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_GENRE,
      placeholder: generateContentPlaceholder('Content Genre'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentID: {
    key: TargetingPresetParamKeys.CONTENT_ID,
    label: 'Content ID',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentID',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_ID,
      TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_ID,
      placeholder: generateContentPlaceholder('Content ID'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentNetwork: {
    key: TargetingPresetParamKeys.CONTENT_NETWORK,
    label: 'Content Network',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentNetwork',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_NETWORK,
      placeholder: generateContentPlaceholder('Content Network'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentProducer: {
    key: TargetingPresetParamKeys.CONTENT_PRODUCER,
    label: 'Content Producer',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentProducer',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_PRODUCER,
      placeholder: generateContentPlaceholder('Content Producer'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentRating: {
    key: TargetingPresetParamKeys.CONTENT_RATING,
    label: 'Content Rating',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentRating',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_RATING,
      TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_RATING,
      placeholder: generateContentPlaceholder('Content Rating'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentSeries: {
    key: TargetingPresetParamKeys.CONTENT_SERIES,
    label: 'Content Series',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentSeries',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_SERIES,
      placeholder: generateContentPlaceholder('Content Series'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentTitle: {
    key: TargetingPresetParamKeys.CONTENT_TITLE,
    label: 'Content Title',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentTitle',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_TITLE,
      placeholder: generateContentPlaceholder('Content Title'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentCategory: {
    key: TargetingPresetParamKeys.CONTENT_CATEGORY,
    label: 'Content Category',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentCategory',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_CATEGORY,
      placeholder: generateContentPlaceholder('Content Category'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentLanguage: {
    key: TargetingPresetParamKeys.CONTENT_LANGUAGE,
    label: 'Content Language',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    selectCategoryTKey: 'Content',
    tKey: 'ContentLanguage',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_LANGUAGE,
      TargetingPresetKeys.TARGETING_CONTENT_LANGUAGE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_LANGUAGE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_LANGUAGE,
      placeholder: generateContentPlaceholder('Content Language'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_LANGUAGE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_LANGUAGE_IS_ALLOWED,
      tooltip: <CustomAdvancedTargetingTooltip />,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_LANGUAGE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_LANGUAGE_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  // STREAMS
  TargetedLivestreams: {
    key: TargetingPresetParamKeys.TARGETED_LIVESTREAMS,
    label: 'Targeted Livestreams',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Streams',
    selectCategoryTKey: 'Livestreams',
    tKey: 'TargetedLivestreams',
    apiKeys: [TargetingPresetKeys.TARGETING_LIVESTREAM],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Livestreams Selected',
      singularLabel: 'Livestream',
      listLabel: 'Livestreams',
      listKey: 'Livestreams',
      valueKey: TargetingPresetKeys.TARGETING_LIVESTREAM,
      convertResponseValue: convertLivestreamResponseValue,
      convertSelectedValue: convertLivestreamSelectedValue,
      key: TargetingPresetKeys.TARGETING_LIVESTREAM,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'Livestreams',
      valueKey: TargetingPresetKeys.TARGETING_LIVESTREAM,
      convertValue: convertNumericIDsForSummary,
    },
  },
  // App and SITE PAGES
  AppBundleIDsSitePages: {
    key: TargetingPresetParamKeys.APP_BUNDLE_IDS_SITE_PAGES,
    label: 'App Bundle IDs & Site Pages',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'App Bundle IDs & Site Pages',
    selectCategoryTKey: 'AppBundleIDsAndSitePages',
    tKey: 'AppBundleIDsAndSitePages',
    apiKeys: [
      TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      TargetingPresetKeys.TARGETING_SITE_PAGES,
      TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
      TargetingPresetKeys.TARGETING_APP_BUNDLE_SITE_PAGES_LOGIC,
    ],
    Component: AppBundleIDsSitePagesEditor,
    props: {
      appBundleIDKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      appBundleIDAllowedKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      sitePagesKey: TargetingPresetKeys.TARGETING_SITE_PAGES,
      sitePagesAllowedKey: TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
      logicKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_SITE_PAGES_LOGIC,
    } as IAppBundleIDsSitePagesEditorProps,
    SummaryComponent: AppBundleSitePagesSummary,
    summaryProps: {
      appBundleIDKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      appBundleIDAllowedKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      sitePagesKey: TargetingPresetKeys.TARGETING_SITE_PAGES,
      sitePagesAllowedKey: TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
      logicKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_SITE_PAGES_LOGIC,
    } as IAppBundleSitePagesSummaryProps,
  },
  SiteDomains: {
    key: TargetingPresetParamKeys.SITE_DOMAINS,
    label: 'Site Domains',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Site Domains',
    selectCategoryTKey: 'SiteDomains',
    tKey: 'SiteDomains',
    apiKeys: [
      TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.SITE_DOMAINS,
      placeholder: generateGenericPlaceholder('Site Domains'),
      valueKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      allowKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      allowKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  // EXTENDED IDS
  // TargetingUID20TargetingType: {
  //   key: TargetingPresetParamKeys.TARGETING_UID20_TARGETING_TYPE,
  //   label: 'UID 2.0',
  //   categoryKey: TargetingPresetCategoryKeys.SUPPLY,
  //   selectCategoryKey: 'Extended IDs',
  //   selectCategoryTKey: 'ExtendedIDs',
  //   tKey: 'UID20',
  //   apiKeys: [TargetingPresetKeys.TARGETING_UID20_TARGETING_TYPE],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_UID20_TARGETING_TYPE,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertSingleNumberIntoArray,
  //     convertSelectedValue: convertSetIntoSingleNumber,
  //     key: TargetingPresetKeys.TARGETING_LIVESTREAM,
  //     disableSort: true,
  //     tooltip: <CustomExtendedIDTooltip />,
  //   } as ISelectWithIncludeAndLogicProps,
  //   SummaryComponent: SelectedOptionsWithLogic,
  //   summaryProps: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_UID20_TARGETING_TYPE,
  //     convertValue: convertSingleNumericIDForSummaryOneDefault,
  //   },
  // },
  // TargetingGooglePairIDTargetingType: {
  //   key: TargetingPresetParamKeys.TARGETING_GOOGLE_PAIR_ID_TARGETING_TYPE,
  //   label: 'Google Pair ID',
  //   categoryKey: TargetingPresetCategoryKeys.SUPPLY,
  //   selectCategoryKey: 'Extended IDs',
  //   selectCategoryTKey: 'ExtendedIDs',
  //   tKey: 'GooglePairID',
  //   apiKeys: [TargetingPresetKeys.TARGETING_GOOGLE_PAIR_ID_TARGETING_TYPE],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_GOOGLE_PAIR_ID_TARGETING_TYPE,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertSingleNumberIntoArray,
  //     convertSelectedValue: convertSetIntoSingleNumber,
  //     key: TargetingPresetKeys.TARGETING_GOOGLE_PAIR_ID_TARGETING_TYPE,
  //     disableSort: true,
  //     tooltip: <CustomExtendedIDTooltip />,
  //   } as ISelectWithIncludeAndLogicProps,
  //   SummaryComponent: SelectedOptionsWithLogic,
  //   summaryProps: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_GOOGLE_PAIR_ID_TARGETING_TYPE,
  //     convertValue: convertSingleNumericIDForSummaryOneDefault,
  //   },
  // },
  // TargetingLiveRampATSTargetingType: {
  //   key: TargetingPresetParamKeys.TARGETING_LIVE_RAMP_ATS_TARGETING_TYPE,
  //   label: 'LiveRamp ATS',
  //   categoryKey: TargetingPresetCategoryKeys.SUPPLY,
  //   selectCategoryKey: 'Extended IDs',
  //   selectCategoryTKey: 'ExtendedIDs',
  //   tKey: 'LiveRampATS',
  //   apiKeys: [TargetingPresetKeys.TARGETING_LIVE_RAMP_ATS_TARGETING_TYPE],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_LIVE_RAMP_ATS_TARGETING_TYPE,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertSingleNumberIntoArray,
  //     convertSelectedValue: convertSetIntoSingleNumber,
  //     key: TargetingPresetKeys.TARGETING_LIVE_RAMP_ATS_TARGETING_TYPE,
  //     disableSort: true,
  //     tooltip: <CustomExtendedIDTooltip />,
  //   } as ISelectWithIncludeAndLogicProps,
  //   SummaryComponent: SelectedOptionsWithLogic,
  //   summaryProps: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_LIVE_RAMP_ATS_TARGETING_TYPE,
  //     convertValue: convertSingleNumericIDForSummaryOneDefault,
  //   },
  // },
  // TargetingIrisSignalTargetingType: {
  //   key: TargetingPresetParamKeys.TARGETING_IRIS_SIGNAL_TARGETING_TYPE,
  //   label: 'Iris Signal',
  //   categoryKey: TargetingPresetCategoryKeys.SUPPLY,
  //   selectCategoryKey: 'Extended IDs',
  //   selectCategoryTKey: 'ExtendedIDs',
  //   tKey: 'IrisSignal',
  //   apiKeys: [TargetingPresetKeys.TARGETING_IRIS_SIGNAL_TARGETING_TYPE],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_IRIS_SIGNAL_TARGETING_TYPE,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertSingleNumberIntoArray,
  //     convertSelectedValue: convertSetIntoSingleNumber,
  //     key: TargetingPresetKeys.TARGETING_IRIS_SIGNAL_TARGETING_TYPE,
  //     disableSort: true,
  //     tooltipTKey: 'IrisSignal',
  //   } as ISelectWithIncludeAndLogicProps,
  //   SummaryComponent: SelectedOptionsWithLogic,
  //   summaryProps: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_IRIS_SIGNAL_TARGETING_TYPE,
  //     convertValue: convertSingleNumericIDForSummaryOneDefault,
  //   },
  // },
  // TargetingSamsungIdentityTargetingType: {
  //   key: TargetingPresetParamKeys.TARGETING_SAMSUNG_IDENTITY_TARGETING_TYPE,
  //   label: 'Samsung Identity',
  //   categoryKey: TargetingPresetCategoryKeys.SUPPLY,
  //   selectCategoryKey: 'Extended IDs',
  //   selectCategoryTKey: 'ExtendedIDs',
  //   tKey: 'SamsungIdentity',
  //   apiKeys: [TargetingPresetKeys.TARGETING_SAMSUNG_IDENTITY_TARGETING_TYPE],
  //   checkIfHidden: (publisherID) => !isPublisherSamsung(publisherID),
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_SAMSUNG_IDENTITY_TARGETING_TYPE,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertSingleNumberIntoArray,
  //     convertSelectedValue: convertSetIntoSingleNumber,
  //     key: TargetingPresetKeys.TARGETING_SAMSUNG_IDENTITY_TARGETING_TYPE,
  //     disableSort: true,
  //     tooltip: <CustomExtendedIDTooltip />,
  //   } as ISelectWithIncludeAndLogicProps,
  //   SummaryComponent: SelectedOptionsWithLogic,
  //   summaryProps: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_SAMSUNG_IDENTITY_TARGETING_TYPE,
  //     convertValue: convertSingleNumericIDForSummaryOneDefault,
  //   },
  // },
  // TargetingSamsungEPGMetadataTargetingType: {
  //   key: TargetingPresetParamKeys.TARGETING_SAMSUNG_EPG_METADATA_TARGETING_TYPE,
  //   label: 'Samsung EPG Metadata',
  //   categoryKey: TargetingPresetCategoryKeys.SUPPLY,
  //   selectCategoryKey: 'Extended IDs',
  //   selectCategoryTKey: 'ExtendedIDs',
  //   tKey: 'SamsungEPGMetadata',
  //   apiKeys: [TargetingPresetKeys.TARGETING_SAMSUNG_EPG_METADATA_TARGETING_TYPE],
  //   checkIfHidden: (publisherID) => !isPublisherSamsung(publisherID),
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_SAMSUNG_EPG_METADATA_TARGETING_TYPE,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertSingleNumberIntoArray,
  //     convertSelectedValue: convertSetIntoSingleNumber,
  //     key: TargetingPresetKeys.TARGETING_SAMSUNG_EPG_METADATA_TARGETING_TYPE,
  //     disableSort: true,
  //     tooltip: <CustomExtendedIDTooltip />,
  //   } as ISelectWithIncludeAndLogicProps,
  //   SummaryComponent: SelectedOptionsWithLogic,
  //   summaryProps: {
  //     listKey: 'ExtendedIDTargetingTypes',
  //     valueKey: TargetingPresetKeys.TARGETING_SAMSUNG_EPG_METADATA_TARGETING_TYPE,
  //     convertValue: convertSingleNumericIDForSummaryOneDefault,
  //   },
  // },
  // USERS CATEGORY
  // GEO
  TargetedRegions: {
    key: TargetingPresetParamKeys.TARGETED_REGIONS,
    label: 'Targeted Regions',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    selectCategoryTKey: 'Geo',
    tKey: 'TargetedRegions',
    apiKeys: [
      TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS,
      TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS_IS_ALLOWED,
    ],
    Component: RegionsEditor,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'CountryGroups',
      valueKey: 'CountryGroups',
      allowKey: TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS_IS_ALLOWED,
      convertValue: convertIDNameForSummary,
    },
  },
  TargetedCountries: {
    key: TargetingPresetParamKeys.TARGETED_COUNTRIES,
    label: 'Targeted Countries',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    selectCategoryTKey: 'Geo',
    tKey: 'TargetedCountries',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Countries',
      singularLabel: 'Country',
      extractStringToSort: removeEmojiPrefixFromLabel,
      listLabel: 'Countries',
      listKey: 'Countries',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
      convertResponseValue: convertStringIntoArray,
      convertSelectedValue: convertSetIntoString,
      key: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
      listKey: 'Countries',
      convertValue: convertCountryIDsForSummary,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  LocalTargetingAllowed: {
    key: TargetingPresetParamKeys.LOCAL_TARGETING_ALLOWED,
    label: 'Local Targeting Included',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    selectCategoryTKey: 'Geo',
    tKey: 'LocalTargetingIncluded',
    apiKeys: [],
    Component: LocalTargetingBuilder,
    props: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_UI,
    } as ILocalTargetingBuilderProps,
    customUIConversions: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_UI,
      convertResponse: parseLocalTargetingForUI,
      convertPayload: convertLocalTargetingUI,
    },
    SummaryComponent: LocalTargetingSummary,
    summaryProps: {
      valueKey: TargetingPresetParamKeys.LOCAL_TARGETING_ALLOWED,
      logicKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_LOGIC,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  LocalTargetingDisallowed: {
    key: TargetingPresetParamKeys.LOCAL_TARGETING_DISALLOWED,
    label: 'Local Targeting Excluded',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    selectCategoryTKey: 'Geo',
    tKey: 'LocalTargetingExcluded',
    apiKeys: [],
    Component: LocalTargetingBuilder,
    props: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_UI,
    } as ILocalTargetingBuilderProps,
    customUIConversions: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_UI,
      convertResponse: parseLocalTargetingForUI,
      convertPayload: convertLocalTargetingUI,
    },
    SummaryComponent: LocalTargetingSummary,
    summaryProps: {
      valueKey: TargetingPresetParamKeys.LOCAL_TARGETING_DISALLOWED,
      logicKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_LOGIC,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  Dayparting: {
    key: TargetingPresetParamKeys.DAYPARTING,
    label: 'Dayparting',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Time',
    selectCategoryTKey: 'Time',
    tKey: 'Dayparting',
    apiKeys: [TargetingPresetKeys.DAYPARTING_IS_ALLOWED],
    Component: DaypartingBuilder,
    customUIConversions: {
      apiKey: TargetingPresetKeys.DAYPARTING,
      uiKey: TargetingPresetKeys.DAYPARTING_UI,
      convertResponse: parseDaypartingForUI,
      convertPayload: convertDaypartingUI,
    },
    SummaryComponent: DaypartingSummary,
    summaryProps: {
      valueKey: TargetingPresetKeys.DAYPARTING,
      allowKey: TargetingPresetKeys.DAYPARTING_IS_ALLOWED,
    },
  },
  // SEGMENTS
  AudienceSegments: {
    key: TargetingPresetParamKeys.AUDIENCE_SEGMENTS,
    label: 'Audience Segments',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Segments',
    selectCategoryTKey: 'Segments',
    tKey: 'AudienceSegments',
    apiKeys: [
      TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      TargetingPresetKeys.SEGMENT_TARGETING,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Audience Segments',
      singularLabel: 'Audience Segment',
      listLabel: 'Audience Segments',
      listKey: 'AudienceSegments',
      valueKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      allowKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      logicKey: TargetingPresetKeys.SEGMENT_TARGETING,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      tooltip: 'Choose which audience segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'AudienceSegments',
      valueKey: 'AudienceSegments',
      allowKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      logicKey: TargetingPresetKeys.SEGMENT_TARGETING,
      convertValue: convertIDNameForSummary,
    },
  },
  ContextualSegments: {
    key: TargetingPresetParamKeys.CONTEXTUAL_SEGMENTS,
    label: 'Contextual Segments',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Segments',
    selectCategoryTKey: 'Segments',
    tKey: 'ContextualSegments',
    apiKeys: [TargetingPresetKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Contextual Segment',
      singularLabel: 'Contextual Segment',
      listLabel: 'Contextual Segments',
      listKey: 'ContextualSegments',
      selectedLimit: 1,
      valueKey: TargetingPresetKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: TargetingPresetKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS,
      tooltip: 'Choose which contextual segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'ContextualSegments',
      valueKey: 'ContextualSegments',
      convertValue: convertIDNameForSummary,
    },
  },
  // PRIVACY
  COPPA: {
    key: TargetingPresetParamKeys.COPPA,
    label: 'COPPA',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    selectCategoryTKey: 'Privacy',
    tKey: 'COPPA',
    apiKeys: [TargetingPresetKeys.TARGETING_COPPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select COPPA Value',
      singularLabel: 'COPPA Value',
      listLabel: 'COPPA',
      listKey: 'COPPA',
      valueKey: TargetingPresetKeys.TARGETING_COPPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_COPPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'COPPA',
      valueKey: TargetingPresetKeys.TARGETING_COPPA,
      convertValue: convertSingleNumericIDForSummaryZeroDefault,
    },
  },
  LimitedAdTracking: {
    key: TargetingPresetParamKeys.LIMITED_AD_TRACKING,
    label: 'Limited Ad Tracking',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    selectCategoryTKey: 'Privacy',
    tKey: 'LimitedAdTracking',
    apiKeys: [TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Limited Ad Tracking',
      singularLabel: 'Limited Ad Tracking',
      listLabel: 'Limited Ad Tracking',
      listKey: 'LimitedAdTracking',
      valueKey: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'LimitedAdTracking',
      valueKey: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      convertValue: convertSingleNumericIDForSummaryZeroDefault,
    },
  },
  CCPA: {
    key: TargetingPresetParamKeys.CCPA,
    label: 'CCPA',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    selectCategoryTKey: 'Privacy',
    tKey: 'CCPA',
    apiKeys: [TargetingPresetKeys.TARGETING_CCPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select CCPA Value',
      singularLabel: 'CCPA Value',
      listLabel: 'CCPA',
      listKey: 'CCPA',
      valueKey: TargetingPresetKeys.TARGETING_CCPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_CCPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'CCPA',
      valueKey: TargetingPresetKeys.TARGETING_CCPA,
      convertValue: convertSingleNumericIDForSummaryZeroDefault,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  GDPR: {
    key: TargetingPresetParamKeys.GDPR,
    label: 'GDPR',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    selectCategoryTKey: 'Privacy',
    tKey: 'GDPR',
    apiKeys: [TargetingPresetKeys.TARGETING_GDPR, TargetingPresetKeys.TARGETING_GDPR_CONSENT],
    Component: GDPRSelect,
    props: {},
    SummaryComponent: GDPRSummary,
    summaryProps: {},
  },
  TargetingDeviceIDType: {
    key: TargetingPresetParamKeys.TARGETING_DEVICE_ID_TYPE,
    label: 'Targeting Device ID Type',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    selectCategoryTKey: 'Privacy',
    tKey: 'TargetingDeviceIDType',
    apiKeys: [TargetingPresetKeys.TARGETING_DEVICE_ID_TYPE],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Targeting Device ID Type',
      singularLabel: 'Targeting Device ID Type',
      listLabel: 'Targeting Device ID Type',
      listKey: 'TargetingDeviceIDType',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_ID_TYPE,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_DEVICE_ID_TYPE,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'TargetingDeviceIDType',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_ID_TYPE,
      convertValue: convertSingleNumericIDForSummaryZeroDefault,
    },
  },
  // CUSTOMS CATEGORY
  // CUSTOM
  Custom4: {
    key: TargetingPresetParamKeys.CUSTOM_4,
    label: 'Custom 4',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom4',
    apiKeys: [TargetingPresetKeys.CUSTOM_4, TargetingPresetKeys.CUSTOM_4_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_4,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_4,
      placeholder: generateCustomPlaceholder('Custom 4'),
      valueKey: TargetingPresetKeys.CUSTOM_4,
      allowKey: TargetingPresetKeys.CUSTOM_4_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_4,
      allowKey: TargetingPresetKeys.CUSTOM_4_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom5: {
    key: TargetingPresetParamKeys.CUSTOM_5,
    label: 'Custom 5',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom5',
    apiKeys: [TargetingPresetKeys.CUSTOM_5, TargetingPresetKeys.CUSTOM_5_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_5,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_5,
      placeholder: generateCustomPlaceholder('Custom 5'),
      valueKey: TargetingPresetKeys.CUSTOM_5,
      allowKey: TargetingPresetKeys.CUSTOM_5_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_5,
      allowKey: TargetingPresetKeys.CUSTOM_5_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom6: {
    key: TargetingPresetParamKeys.CUSTOM_6,
    label: 'Custom 6',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom6',
    apiKeys: [TargetingPresetKeys.CUSTOM_6, TargetingPresetKeys.CUSTOM_6_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_6,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_6,
      placeholder: generateCustomPlaceholder('Custom 6'),
      valueKey: TargetingPresetKeys.CUSTOM_6,
      allowKey: TargetingPresetKeys.CUSTOM_6_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_6,
      allowKey: TargetingPresetKeys.CUSTOM_6_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom7: {
    key: TargetingPresetParamKeys.CUSTOM_7,
    label: 'Custom 7',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom7',
    apiKeys: [TargetingPresetKeys.CUSTOM_7, TargetingPresetKeys.CUSTOM_7_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_7,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_7,
      placeholder: generateCustomPlaceholder('Custom 7'),
      valueKey: TargetingPresetKeys.CUSTOM_7,
      allowKey: TargetingPresetKeys.CUSTOM_7_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_7,
      allowKey: TargetingPresetKeys.CUSTOM_7_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom8: {
    key: TargetingPresetParamKeys.CUSTOM_8,
    label: 'Custom 8',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom8',
    apiKeys: [TargetingPresetKeys.CUSTOM_8, TargetingPresetKeys.CUSTOM_8_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_8,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_8,
      placeholder: generateCustomPlaceholder('Custom 8'),
      valueKey: TargetingPresetKeys.CUSTOM_8,
      allowKey: TargetingPresetKeys.CUSTOM_8_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_8,
      allowKey: TargetingPresetKeys.CUSTOM_8_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom9: {
    key: TargetingPresetParamKeys.CUSTOM_9,
    label: 'Custom 9',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom9',
    apiKeys: [TargetingPresetKeys.CUSTOM_9, TargetingPresetKeys.CUSTOM_9_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_9,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_9,
      placeholder: generateCustomPlaceholder('Custom 9'),
      valueKey: TargetingPresetKeys.CUSTOM_9,
      allowKey: TargetingPresetKeys.CUSTOM_9_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_9,
      allowKey: TargetingPresetKeys.CUSTOM_9_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom10: {
    key: TargetingPresetParamKeys.CUSTOM_10,
    label: 'Custom 10',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom10',
    apiKeys: [TargetingPresetKeys.CUSTOM_10, TargetingPresetKeys.CUSTOM_10_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_10,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_10,
      placeholder: generateCustomPlaceholder('Custom 10'),
      valueKey: TargetingPresetKeys.CUSTOM_10,
      allowKey: TargetingPresetKeys.CUSTOM_10_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_10,
      allowKey: TargetingPresetKeys.CUSTOM_10_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom11: {
    key: TargetingPresetParamKeys.CUSTOM_11,
    label: 'Custom 11',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom11',
    apiKeys: [TargetingPresetKeys.CUSTOM_11, TargetingPresetKeys.CUSTOM_11_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_11,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_11,
      placeholder: generateCustomPlaceholder('Custom 11'),
      valueKey: TargetingPresetKeys.CUSTOM_11,
      allowKey: TargetingPresetKeys.CUSTOM_11_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_11,
      allowKey: TargetingPresetKeys.CUSTOM_11_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom12: {
    key: TargetingPresetParamKeys.CUSTOM_12,
    label: 'Custom 12',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom12',
    apiKeys: [TargetingPresetKeys.CUSTOM_12, TargetingPresetKeys.CUSTOM_12_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_12,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_12,
      placeholder: generateCustomPlaceholder('Custom 12'),
      valueKey: TargetingPresetKeys.CUSTOM_12,
      allowKey: TargetingPresetKeys.CUSTOM_12_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_12,
      allowKey: TargetingPresetKeys.CUSTOM_12_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom13: {
    key: TargetingPresetParamKeys.CUSTOM_13,
    label: 'Custom 13',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom13',
    apiKeys: [TargetingPresetKeys.CUSTOM_13, TargetingPresetKeys.CUSTOM_13_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_13,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_13,
      placeholder: generateCustomPlaceholder('Custom 13'),
      valueKey: TargetingPresetKeys.CUSTOM_13,
      allowKey: TargetingPresetKeys.CUSTOM_13_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_13,
      allowKey: TargetingPresetKeys.CUSTOM_13_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom14: {
    key: TargetingPresetParamKeys.CUSTOM_14,
    label: 'Custom 14',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom14',
    apiKeys: [TargetingPresetKeys.CUSTOM_14, TargetingPresetKeys.CUSTOM_14_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_14,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_14,
      placeholder: generateCustomPlaceholder('Custom 14'),
      valueKey: TargetingPresetKeys.CUSTOM_14,
      allowKey: TargetingPresetKeys.CUSTOM_14_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_14,
      allowKey: TargetingPresetKeys.CUSTOM_14_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom15: {
    key: TargetingPresetParamKeys.CUSTOM_15,
    label: 'Custom 15',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom15',
    apiKeys: [TargetingPresetKeys.CUSTOM_15, TargetingPresetKeys.CUSTOM_15_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_15,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_15,
      placeholder: generateCustomPlaceholder('Custom 15'),
      valueKey: TargetingPresetKeys.CUSTOM_15,
      allowKey: TargetingPresetKeys.CUSTOM_15_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_15,
      allowKey: TargetingPresetKeys.CUSTOM_15_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom16: {
    key: TargetingPresetParamKeys.CUSTOM_16,
    label: 'Custom 16',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom16',
    apiKeys: [TargetingPresetKeys.CUSTOM_16, TargetingPresetKeys.CUSTOM_16_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_16,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_16,
      placeholder: generateCustomPlaceholder('Custom 16'),
      valueKey: TargetingPresetKeys.CUSTOM_16,
      allowKey: TargetingPresetKeys.CUSTOM_16_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_16,
      allowKey: TargetingPresetKeys.CUSTOM_16_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom17: {
    key: TargetingPresetParamKeys.CUSTOM_17,
    label: 'Custom 17',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom17',
    apiKeys: [TargetingPresetKeys.CUSTOM_17, TargetingPresetKeys.CUSTOM_17_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_17,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_17,
      placeholder: generateCustomPlaceholder('Custom 17'),
      valueKey: TargetingPresetKeys.CUSTOM_17,
      allowKey: TargetingPresetKeys.CUSTOM_17_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_17,
      allowKey: TargetingPresetKeys.CUSTOM_17_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom18: {
    key: TargetingPresetParamKeys.CUSTOM_18,
    label: 'Custom 18',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'Custom18',
    apiKeys: [TargetingPresetKeys.CUSTOM_18, TargetingPresetKeys.CUSTOM_18_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_18,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_18,
      placeholder: generateCustomPlaceholder('Custom 18'),
      valueKey: TargetingPresetKeys.CUSTOM_18,
      allowKey: TargetingPresetKeys.CUSTOM_18_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_18,
      allowKey: TargetingPresetKeys.CUSTOM_18_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  CustomTargeting: {
    key: TargetingPresetParamKeys.CUSTOM_TARGETING,
    label: 'Custom Targeting',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    selectCategoryTKey: 'Custom',
    tKey: 'CustomTargeting',
    apiKeys: [TargetingPresetKeys.CUSTOM_TARGETING, TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_TARGETING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_TARGETING,
      placeholder: generateCustomPlaceholder('Custom Targeting'),
      valueKey: TargetingPresetKeys.CUSTOM_TARGETING,
      allowKey: TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_TARGETING,
      allowKey: TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
};

export interface ITargetingPresetCategory {
  key: TargetingPresetCategory; // also acts as label
  paramKeys: TargetingPresetParamKey[];
  iconName: string;
  subText: string;
  addParamText: string;
}

export const targetingPresetCategoriesAndParameters: Record<
  TargetingPresetCategory,
  ITargetingPresetCategory
> = {
  Supply: {
    key: 'Supply',
    paramKeys: [
      'TargetedDevices',
      'TargetedDeviceTypes',
      'TargetedDeviceSubtypes',
      'IPFilter',
      'ContentChannel',
      'ContentDistributor',
      'ContentGenre',
      'ContentID',
      'ContentNetwork',
      'ContentProducer',
      'ContentRating',
      'ContentSeries',
      'ContentTitle',
      'ContentCategory',
      'ContentLanguage',
      'TargetedLivestreams',
      'AppBundleIDsSitePages',
      'SiteDomains',
      // 'TargetingUID20TargetingType',
      // 'TargetingGooglePairIDTargetingType',
      // 'TargetingLiveRampATSTargetingType',
      // 'TargetingIrisSignalTargetingType',
      // 'TargetingSamsungIdentityTargetingType',
      // 'TargetingSamsungEPGMetadataTargetingType',
    ],
    iconName: 'cast',
    subText:
      'All Devices, All Content Params, All Stream, All Apps , All Sites Pages , All Site Domains',
    addParamText: '+ Add Supply Parameter',
  },
  Users: {
    key: 'Users',
    paramKeys: [
      'TargetedRegions', // COUNTRY GROUPS
      'TargetedCountries',
      'LocalTargetingAllowed',
      'LocalTargetingDisallowed',
      'Dayparting',
      'AudienceSegments',
      'ContextualSegments',
      'COPPA',
      'LimitedAdTracking',
      'CCPA',
      'GDPR',
      'TargetingDeviceIDType',
    ],
    iconName: 'eye',
    subText: ' All Geos, All Time (Dayparting), All Segments, Privacy',
    addParamText: '+ Add User Parameter',
  },

  Customs: {
    key: 'Customs',
    paramKeys: [
      'Custom4',
      'Custom5',
      'Custom6',
      'Custom7',
      'Custom8',
      'Custom9',
      'Custom10',
      'Custom11',
      'Custom12',
      'Custom13',
      'Custom14',
      'Custom15',
      'Custom16',
      'Custom17',
      'Custom18',
      'CustomTargeting',
    ],
    iconName: 'code',
    subText: 'All Custom Settings',
    addParamText: '+ Add Custom Parameter',
  },
};
export interface IParameterDropdown {
  key: string;
  getList: (session: ISessionState, user: IUserState) => Promise<ISelectOption[]>;
}

export const targetingPresetDropdowns = {
  Countries: {
    key: 'Countries',
    getList: () => generateOptionsListAsPromise(generateCountryOptions()),
  },
  CountryGroups: {
    key: 'CountryGroups',
    getList: generateCountryGroupOptionsList,
  },
  DMAs: {
    key: 'DMAs',
    getList: () => generateOptionsListAsPromise(generateDMAOptions('us')),
  },
  COPPA: {
    key: 'COPPA',
    getList: () => generateOptionsListAsPromise(TargetingCOPPAOptions),
  },
  LimitedAdTracking: {
    key: 'LimitedAdTracking',
    getList: () => generateOptionsListAsPromise(TargetBoolOptions),
  },
  CCPA: {
    key: 'CCPA',
    getList: () => generateOptionsListAsPromise(TargetingCCPAOptions),
  },
  GDPR: {
    key: 'GDPR',
    getList: () => generateOptionsListAsPromise(TargetingGDPROptions),
  },
  GDPRConsent: {
    key: 'GDPRConsent',
    getList: () => generateOptionsListAsPromise(TargetingGDPRConsentOptions),
  },
  TargetingDeviceIDType: {
    key: 'TargetingDeviceIDType',
    getList: () => generateOptionsListAsPromise(TargetingDeviceIDTypeOptions),
  },
  Devices: {
    key: 'Devices',
    getList: () => generateOptionsListAsPromise(DeviceOptions),
  },
  DeviceTypes: {
    key: 'DeviceTypes',
    getList: () => generateOptionsListAsPromise(DeviceORTBTypeOptions),
  },
  DeviceSubTypes: {
    key: 'DeviceSubTypes',
    getList: generateDeviceSubTypeOptionsList,
  },
  Livestreams: {
    key: 'Livestreams',
    getList: () => generateOptionsListAsPromise(TargetingLiveStreamOptions),
  },
  AudienceSegments: {
    key: 'AudienceSegments',
    getList: generateAudienceSegmentOptionsList,
  },
  ContextualSegments: {
    key: 'ContextualSegments',
    getList: generateContextualSegmentOptionsList,
  },
  ExtendedIDTargetingTypes: {
    key: 'ExtendedIDTargetingTypes',
    getList: () => generateOptionsListAsPromise(ExtendedIDTargetingTypeOptions),
  },
} as Record<string, IParameterDropdown>;

export interface ITargetingPresetAssociatedBadge {
  dataKey: keyof ITargetingPreset;
  singularLabel: string;
  pluralLabel: ItemRouteType;
  color: string;
  iconName: string;
  modalTitle: string;
}

export interface ITargetingPresetFormConfig {
  badges: ITargetingPresetAssociatedBadge[];
  categories: Record<TargetingPresetCategory, ITargetingPresetCategory>;
  params: Record<TargetingPresetParamKey, ITargetingPresetParam>;
  dropdowns: Record<string, IParameterDropdown>;
}

export const targetingPresetFormConfig: ITargetingPresetFormConfig = {
  badges: [
    {
      dataKey: 'Bidders',
      singularLabel: 'Bidder',
      pluralLabel: 'Bidders',
      color: publicaColors.honeydew,
      iconName: 'bidders',
      modalTitle: 'Associated Bidders',
    },
    {
      dataKey: 'Campaigns',
      singularLabel: 'Campaign',
      pluralLabel: 'Campaigns',
      color: publicaColors.lavender,
      iconName: 'paper-plane',
      modalTitle: 'Associated Campaigns',
    },
  ],
  categories: targetingPresetCategoriesAndParameters,
  params: TargetingPresetParameters,
  dropdowns: targetingPresetDropdowns,
};

export const generateTargetingPresetParameterOptions = (
  optionKeys: TargetingPresetParamKey[],
  publisherID: string,
  isSuperUser: boolean,
): IParameterOption[] => {
  const options: any[] = [];
  let currentCategory = '' as TargetingPresetSelectCategory;
  optionKeys.forEach((key) => {
    const { label, selectCategoryKey, isSuperUserOnly, checkIfHidden, tKey, selectCategoryTKey } =
      TargetingPresetParameters[key];
    if (selectCategoryKey !== currentCategory) {
      currentCategory = selectCategoryKey;
      options.push({
        key: selectCategoryKey,
        label: selectCategoryKey,
        isCategory: true,
        category: selectCategoryKey,
        tKey: selectCategoryTKey,
      });
    }
    if (isSuperUserOnly && !isSuperUser) return;
    if (checkIfHidden && checkIfHidden(publisherID, isSuperUser)) return;
    options.push({
      key,
      label,
      category: selectCategoryKey,
      tKey,
    });
  });
  return options;
};

export const emptyActiveParametersShell = {
  Supply: [],
  Users: [],
  Customs: [],
} as Record<TargetingPresetCategory, TargetingPresetParamKey[]>;

export const extractSuperUserOnlyTargetingPresetParamKeys = (): TargetingPresetParamKey[] => {
  const result: TargetingPresetParamKey[] = [];
  Object.values(TargetingPresetParameters).forEach((param) => {
    if (param.isSuperUserOnly) {
      result.push(param.key);
    }
  });
  return result;
};
